import type { DataGridColumnOrderState, DataGridFilterState, DataGridSortingState } from '@ibsys/kit';
import { FC, useCallback, useMemo, useRef, useState } from 'react';
import type { UserUrlLogItem, UserUrlLogListParams } from '@/entities/user-url-log';
import { useLazyUserUrlLog } from '@/entities/user-url-log';
import { PageContainer } from '@/shared/ui';
import { UserUrlLogClearControl } from './ui/UserUrlLogClearControl';
import { UserUrlLogTable } from './ui/UserUrlLogTable';

// Размер порции infinite scroll: фиксированный limit, offset растёт при прокрутке.
const PAGE_SIZE = 50;

// Порядок колонок по умолчанию; id колонок совпадают с ключами сортировки бэкенда.
const DEFAULT_COLUMN_ORDER: DataGridColumnOrderState = ['user', 'impersonatedUser', 'url', 'createdAt'];

// Трансляция controlled-стейта DataGrid (сортировка/фильтры) в query-параметры GET /user-url-log.
// camelCase-ключи кебаб-кейсятся apiClient'ом (userId → user-id и т. д.); пустые значения опускаются.
const toQueryParams = (sorting: DataGridSortingState, filters: DataGridFilterState): UserUrlLogListParams => {
  const activeSort = sorting[0];
  const range = filters.createdAt as { from?: string; to?: string } | undefined;
  const urlPattern = (filters.url as string | undefined)?.trim();

  return {
    sort: activeSort ? `${activeSort.columnId}:${activeSort.direction}` : undefined,
    userId: (filters.user as number | undefined) ?? undefined,
    impersonatedUserId: (filters.impersonatedUser as number | undefined) ?? undefined,
    url: urlPattern || undefined,
    createdAtFrom: range?.from || undefined,
    createdAtTo: range?.to || undefined,
  };
};

/**
 * Контейнер страницы лога URL: держит состояние серверной сортировки, фильтров
 * и (клиентского) порядка колонок, тянет порции через lazy-запрос для бесконечной прокрутки
 * и отдаёт презентеру с DataGrid в lazy-режиме.
 */
export const UserUrlLogPage: FC = () => {
  const [sorting, setSorting] = useState<DataGridSortingState>([]);
  const [filters, setFilters] = useState<DataGridFilterState>({});
  const [columnOrder, setColumnOrder] = useState<DataGridColumnOrderState>(DEFAULT_COLUMN_ORDER);
  const [totalCount, setTotalCount] = useState(0);
  // Бамп после очистки лога: меняет cacheVersion → DataGrid сбрасывает кеш и перезапрашивает список.
  const [refreshToken, setRefreshToken] = useState(0);

  const { refetch } = useLazyUserUrlLog();

  // Сигнатура запроса: смена сортировки/фильтров (или очистка лога) меняет cacheVersion → DataGrid
  // сбрасывает внутренний кеш страниц и перезапрашивает список с первой порции (offset = 0).
  const cacheVersion = useMemo(
    () => `${JSON.stringify(sorting)}|${JSON.stringify(filters)}|${refreshToken}`,
    [sorting, filters, refreshToken],
  );

  // При смене сигнатуры сбрасываем totalCount: DataGrid lazy высчитывает limit как
  // min(pageSize, totalCount - offset); устаревший totalCount после применённого фильтра
  // приводит к нестандартному limit, не попадающему в общий кеш с дефолтным запросом.
  const lastCacheVersionRef = useRef(cacheVersion);
  if (lastCacheVersionRef.current !== cacheVersion) {
    lastCacheVersionRef.current = cacheVersion;
    setTotalCount(0);
  }

  const fetchPage = useCallback(
    async (offset: number, limit: number): Promise<UserUrlLogItem[]> => {
      const data = await refetch({ ...toQueryParams(sorting, filters), offset, limit });
      setTotalCount(data.total);
      return data.items;
    },
    [refetch, sorting, filters],
  );

  return (
    <PageContainer enableScroll={false} className="flex flex-col gap-4 h-full w-full">
      <UserUrlLogTable
        totalCount={totalCount}
        pageSize={PAGE_SIZE}
        fetchPage={fetchPage}
        cacheVersion={cacheVersion}
        sorting={sorting}
        filters={filters}
        columnOrder={columnOrder}
        onSortingChange={setSorting}
        onFiltersChange={setFilters}
        onColumnOrderChange={setColumnOrder}
        clearControl={<UserUrlLogClearControl onCleared={() => setRefreshToken((prev) => prev + 1)} />}
      />
    </PageContainer>
  );
};
